<template>
  <v-tab-item :value="$route.fullPath" class="pa-3">
    <v-toolbar flat height="190">
      <v-btn
        :color="superUser || permissions.products.create ? 'secondary' : 'grey'"
        class="text-capitalize px-5"
        depressed
        v-text="translations.addProduct[lang]"
        :disabled="!superUser && !permissions.products.create"
        @click="superUser || permissions.products.create ? open(1, {}) : setPermissionDialog(true)"
      ></v-btn>

      <v-spacer></v-spacer>

      <v-col cols="4" class="pa-0">
        <v-text-field
          v-model="search"
          class="mt-1"
          :label="translations.search[lang]"
          color="secondary"
          outlined
          clearable
          rounded
          dense
          hide-details
          :prepend-inner-icon="icons.mdiMagnify"
        >
        </v-text-field>
      </v-col>
    </v-toolbar>
    <v-data-table
      :loading="loading"
      :headers="headers"
      :items="products"
      :footer-props="{
        itemsPerPageOptions: [12],
        showCurrentPage: true,
        pageText: `${page * 12 - 11}-${
          page * 12 <= serverItemsLength ? page * 12 : serverItemsLength
        } ${translations.paginationText[lang]} ${serverItemsLength}`,
      }"
      :page.sync="page"
      :server-items-length="serverItemsLength"
      class="b-outlined"
      :options.sync="options"
    >
      <template v-slot:no-data>
        <span v-if="!loading" v-text="translations.noData[lang]"></span>
      </template>

      <template v-slot:item.image="{ item }">
        <v-img :src="item.image" width="70" v-if="item.image"></v-img>
        <div v-else>-</div>
      </template>

      <template v-slot:item.sku_number="{ item }">
        {{ item.sku_number || '-' }}
      </template>

      <template v-slot:item.retail_price="{ item }">
        {{
          new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 4,
          }).format(item.retail_price)
        }}
      </template>

      <template v-slot:item.published="{ item }">
        <v-icon v-if="item.published" color="green" v-text="icons.mdiCheckCircleOutline"></v-icon>
        <v-icon v-else color="red" v-text="icons.mdiMinusCircleOutline"></v-icon>
      </template>

      <template v-slot:item.actions="{ item }">
        <v-tooltip color="secondary" top>
          <template v-slot:activator="{ on }">
            <v-btn
              :color="
                item.parent_product_id
                  ? superUser || permissions.company_products.update
                    ? 'yellow darken-3'
                    : 'grey'
                  : permissions.products &&
                      permissions.products.update &&
                      storePermissions.products.update
                    ? 'yellow darken-3'
                    : 'grey'
              "
              icon
              v-on="on"
              @click="
                item.parent_product_id
                  ? superUser || permissions.company_products.update
                    ? open(2, item)
                    : setPermissionDialog(true)
                  : superUser || (permissions.products.update && storePermissions.products.update)
                    ? open(2, item)
                    : setPermissionDialog(true)
              "
            >
              <v-icon v-text="icons.mdiPencilOutline"></v-icon>
            </v-btn>
          </template>

          <span class="font-weight-bold" v-text="translations.update[lang]"></span>
        </v-tooltip>

        <v-tooltip color="secondary" top>
          <template v-slot:activator="{ on }">
            <v-btn
              :color="
                item.parent_product_id
                  ? superUser || permissions.company_products.delete
                    ? 'red'
                    : 'grey'
                  : superUser || (permissions.products.delete && storePermissions.products.delete)
                    ? 'red'
                    : 'grey'
              "
              icon
              v-on="on"
              @click="
                () => {
                  if (item.parent_product_id) {
                    if (superUser || permissions.company_products.delete) {
                      product = item;
                      deleteDialog = true;
                    } else {
                      setPermissionDialog(true);
                    }
                  } else {
                    if (
                      superUser ||
                      (permissions.products.delete && storePermissions.products.delete)
                    ) {
                      product = item;
                      deleteDialog = true;
                    } else {
                      setPermissionDialog(true);
                    }
                  }
                }
              "
            >
              <v-icon v-text="icons.mdiClose"></v-icon>
            </v-btn>
          </template>

          <span class="font-weight-bold" v-text="translations.delete[lang]"></span>
        </v-tooltip>
      </template>
    </v-data-table>

    <v-dialog v-model="dialog" max-width="600">
      <ProductForm :mode="mode" @cancel="dialog = false" :initial-values="initialValues" />
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="500">
      <b-card
        type="delete"
        :title="translations.deleteProduct[lang]"
        :submit-text="{ el: '', en: 'delete', it: '' }"
        :loading="loading"
        :error-message="errorMessage"
        @cancel="deleteDialog = false"
        @submit="remove"
      >
        <div class="subtitle-1 font-weight-medium py-3 px-2">
          {{ translations.deleteText[lang] }}
          <span class="font-weight-bold text--primary">{{ product.name[lang] }}</span
          >?
        </div>
      </b-card>
    </v-dialog>
  </v-tab-item>
</template>

<script>
import products from '@/store/modules/storePanel/products';

import {
  mdiCheckCircleOutline,
  mdiMinusCircleOutline,
  mdiPencilOutline,
  mdiClose,
  mdiMagnify,
} from '@mdi/js';

import { mapState, mapMutations, mapActions } from 'vuex';
import debounce from 'lodash/debounce';
import ProductForm from '@/components/storePanel/products/ProductForm.vue';
import translations from '@/utils/translations/storePanel/products';

export default {
  name: 'ProductsTab',

  components: { ProductForm },

  mixins: [translations],

  data() {
    return {
      icons: {
        mdiCheckCircleOutline,
        mdiMinusCircleOutline,
        mdiPencilOutline,
        mdiClose,
        mdiMagnify,
      },
      page: +this.$route.query.page,
      mode: 0,
      search: '',
      filtersData: {
        orderBy: null,
        orderPriority: null,
      },
      options: {},
      initialValues: {},
    };
  },

  computed: {
    ...mapState([
      'loading',
      'errorMessage',
      'serverItemsLength',
      'permissions',
      'storePermissions',
      'superUser',
    ]),
    ...mapState('storePanel/products', ['products']),

    lang() {
      return this.$route.params.lang;
    },
    storeMode() {
      return this.$store.state.store?.display_mode || 'points';
    },
    headers() {
      return [
        {
          text: this.translations.image[this.lang],
          value: 'image',
          sortable: false,
        },
        {
          text: this.translations.productName[this.lang],
          value: `name[${this.lang}]`,
        },
        {
          text: this.translations.productDescription[this.lang],
          value: `description[${this.lang}]`,
        },
        {
          text: 'SKU',
          value: 'sku_number',
        },
        {
          text: this.translations.sellingPrice[this.lang],
          value: 'retail_price',
        },
        {
          text: this.translations[this.storeMode]?.[this.lang],
          value: 'reward_points',
        },
        {
          text: this.translations.category[this.lang],
          value: `product_category.name[${this.lang}]`,
        },
        {
          text: this.translations.published[this.lang],
          value: 'published',
          align: 'center',
        },
        {
          text: this.translations.actions[this.lang],
          value: 'actions',
          sortable: false,
        },
      ];
    },

    dialog: {
      get() {
        return this.$store.state.dialog;
      },

      set(val) {
        this.setDialog(val);
      },
    },

    deleteDialog: {
      get() {
        return this.$store.state.deleteDialog;
      },

      set(val) {
        this.setDeleteDialog(val);
      },
    },

    product: {
      get() {
        return this.$store.state.storePanel.products.product;
      },

      set(val) {
        this.setItem(val);
      },
    },

    query() {
      let query = '?';

      for (let key in this.$route.query) {
        query += `${key}=${this.$route.query[key]}&`;
      }

      return query.slice(0, query.length - 1);
    },
  },

  methods: {
    ...mapMutations([
      'setDialog',
      'setDeleteDialog',
      'setResetSuccess',
      'setResetValidation',
      'setPermissionDialog',
    ]),
    ...mapMutations('storePanel/products', [
      'setShowImageUpload',
      'setShowWeekdays',
      'setItem',
      'setItems',
    ]),
    ...mapActions('storePanel/products', ['getItems', 'remove']),

    open(mode, item) {
      if (mode == 2) {
        item.product_category_id = item.product_category
          ? item.product_category.product_category_id
          : null;
        this.initialValues = JSON.parse(JSON.stringify(item));
      }
      this.mode = mode;
      this.product = item;
      if (this.product.image) this.setShowImageUpload(true);
      else this.setShowImageUpload(false);
      if (this.product.availability_days.length) this.setShowWeekdays(true);
      else this.setShowWeekdays(false);
      setTimeout(() => this.setResetSuccess(true), 300);
      this.setResetValidation(true);
      this.dialog = true;
    },

    handleSearch() {
      let sendingData = {
        query: `?q=${this.search}`,
        filters: this.filtersData,
      };
      this.getItems(sendingData);
    },

    getFilteredData() {
      let sendingData = {
        query: this.query,
        filters: this.filtersData,
      };
      this.getItems(sendingData);
    },
  },

  watch: {
    options: {
      handler(val) {
        if (val.sortBy[0]) {
          switch (val.sortBy[0]) {
            case `name[${this.lang}]`:
              this.filtersData.orderBy = 'product_name';
              break;
            case `description[${this.lang}]`:
              this.filtersData.orderBy = 'product_description';
              break;
            case 'retail_price':
              this.filtersData.orderBy = 'selling_price';
              break;
            case 'reward_points':
              this.filtersData.orderBy = 'points';
              break;
            case 'category':
              this.filtersData.orderBy = `product_category.name[${this.lang}]`;
              break;
            case 'published':
              this.filtersData.orderBy = 'published';
              break;
          }

          if (val.sortDesc) {
            this.filtersData.orderPriority = val.sortDesc[0] ? 'desc' : 'asc';
          }
          this.getFilteredData();
        }
      },
      deep: true,
    },
    $route(val) {
      if (!val.query.page) {
        this.$router.replace({
          query: {
            page: 1,
            ...this.$route.query,
          },
        });
      }
      this.getFilteredData();
    },

    page(page) {
      this.$router.replace({ query: { ...this.$route.query, page } });
    },

    search(val, oldVal) {
      if (val != oldVal) {
        if (val == null) {
          this.getFilteredData();
        } else {
          this.debouncedSearch();
        }
      }
    },
  },

  beforeCreate() {
    if (!this.$store.state.storePanel.products) {
      this.$store.registerModule(['storePanel', 'products'], products);
    }

    if (!this.$route.query.page) {
      this.$router.replace({
        query: {
          page: 1,
          ...this.$route.query,
        },
      });
    }
  },

  created() {
    this.debouncedSearch = debounce(this.handleSearch, 500);
  },

  mounted() {
    this.getFilteredData();
  },
};
</script>
